import { useConfirm } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useFixedFeeTermAcceptanceLinkActions } from '../../../store/fixedFeeTermAcceptanceLink/action';
import {
  fixedFeePublicAuthorizationTokenState,
  fixedFeePublicTermIdState,
} from '../../../store/fixedFeeTermAcceptanceLink/atom';
import { fixedFeePublicTermQuery } from '../../../store/fixedFeeTermAcceptanceLink/selector';
import { FixedFeeTermAcceptanceData } from '../utils/types';

export const useFixedFeeTermAcceptance = () => {
  const { confirm } = useConfirm();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [decision, setDecision] = React.useState<'accepted' | 'refused'>();

  const termId = useRecoilValue(fixedFeePublicTermIdState);
  const authorizationToken = useRecoilValue(fixedFeePublicAuthorizationTokenState);
  const jwtToken = authorizationToken.jwtToken || '';

  const { acceptTerm, refuseTerm, downloadTerm } = useFixedFeeTermAcceptanceLinkActions();

  const {
    data: response,
    error,
    loading,
    refresher,
  } = useRecoilQuery(
    fixedFeePublicTermQuery({
      termId,
      jwtToken,
    }),
    {
      if: () => !!authorizationToken.jwtToken && !!termId,
    }
  );

  const handleDownloadTerm = (term: FixedFeeTermAcceptanceData) => {
    downloadTerm({ fileName: term.termsFilename, jwtToken });
  };

  const handleAccept = (term: FixedFeeTermAcceptanceData) => {
    const isRevocationFlow = term.actionType === 'revocation';

    confirm(
      isRevocationFlow
        ? {
            isMobile,
            title: 'Atenção',
            description:
              'Ao confirmar o encerramento você retornará ao modelo padrão de comissionamento.',
            cancelText: 'Voltar',
            confirmText: 'Confirmar',
            confirmColor: 'feedback.error.default',
            icon: 'Warning',
            iconColor: 'feedback.attention.default',
          }
        : {
            isMobile,
            title: 'Período de permanência',
            description:
              'Após o aceite, haverá um período de permanência de 30 dias para encerramento ou substituição do modelo.',
            cancelText: 'Voltar',
            confirmText: 'Confirmar',
            confirmColor: 'brand.tertiary.principal',
            hideIcon: true,
          },
      async () => {
        await acceptTerm({ termId, jwtToken });
        setDecision('accepted');
      }
    );
  };

  const handleRefuse = (term: FixedFeeTermAcceptanceData) => {
    const isRevocationFlow = term.actionType === 'revocation';

    confirm(
      isRevocationFlow
        ? {
            isMobile,
            title: 'Atenção',
            description:
              'Ao continuar ativo, você recusará a solicitação de encerramento deste termo. Deseja continuar?',
            cancelText: 'Voltar',
            confirmText: 'Confirmar',
            confirmColor: 'brand.tertiary.principal',
            hideIcon: true,
          }
        : {
            isMobile,
            title: 'Atenção',
            description:
              'Ao recusar este termo, você precisará pedir ao seu assessor o envio de um novo para dar continuidade. Quer confirmar a recusa?',
            cancelText: 'Voltar',
            confirmText: 'Confirmar',
            confirmColor: 'feedback.error.default',
            icon: 'Warning',
            iconColor: 'feedback.attention.default',
          },
      async () => {
        await refuseTerm({ termId, jwtToken });
        setDecision('refused');
      }
    );
  };

  return {
    termData: response?.data,
    decision,
    error,
    loading,
    retry: refresher,
    handleAccept,
    handleRefuse,
    handleDownloadTerm,
  };
};
